import React, { useEffect, useMemo, useState, forwardRef,
  useImperativeHandle } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  StyleSheet,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import DOBTextInputComponent from "../../../common/DOBTextInputComponent";
import {
  validateDobDateFromDisplayString,
  formatDobInput,
  dateFromDobDisplayString,
  isDobDisplayComplete,
  DOB_MIN_AGE_SEND_OR_BILLPAY,
  DOB_MAX_AGE_YEARS,
} from "../../../../../helper/dobValidation";

const BILLPAY_DOB_AGE_OPTIONS = {
  minimumAgeYears: DOB_MIN_AGE_SEND_OR_BILLPAY,
  maximumAgeYears: DOB_MAX_AGE_YEARS,
} as const;

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string;
};

export interface BillPayDOBStepHandle {
  submit: () => Promise<boolean>;
}

const BillPayDOBStep = forwardRef<BillPayDOBStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      dob: "",
    });
  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString) || "";
  const [dob, setDob] = useState(
    () => (useSessionStore.getState() as any).senderDOBString || ""
  );
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);

  const isValid = useMemo(() => {
    if (!isDobDisplayComplete(dob)) return false;
    return !validateDobDateFromDisplayString(t, dob.trim(), true, BILLPAY_DOB_AGE_OPTIONS);
  }, [dob, t]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!dob.trim()) {
        const next = { dob: t("SENDER_DOB_ERROR_REQUIRED") };
        setErrors(next);
        return false;
      }
      if (isDobDisplayComplete(dob)) {
        const err = validateDobDateFromDisplayString(t, dob.trim(), true, BILLPAY_DOB_AGE_OPTIONS);
        const next = { dob: err };
        setErrors(next);
        return !Boolean(err);
      }
      const next = { dob: t("SENDER_DOB_ERROR_INVALID") };
      setErrors(next);
      return false;
    },
  }));
    
  // Keep local DOB in sync when store is hydrated (e.g., after AuthConsumer)
  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  // Mock ID verification function (replace with real API call)
  const verifyDOBWithID = async (enteredDob: string) => {
    // Simulate backend check with previously uploaded ID
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 1000) // always returns true for now
    );
  };

  const handleManualInput = async (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setSenderDOBString?.(formatted); } catch {}
    if (isDobDisplayComplete(formatted)) {
      const err = validateDobDateFromDisplayString(t, formatted, true, BILLPAY_DOB_AGE_OPTIONS);
      setErrors((prev) => ({ ...prev, dob: err }));
      if (!err) {
        const parsed = dateFromDobDisplayString(formatted);
        if (parsed) {
          try { setSenderDOB?.(parsed); } catch {}
          await verifyDOBWithID(formatted);
        }
      }
    } else if (!formatted.trim()) {
      setErrors((prev) => ({ ...prev, dob: t("SENDER_DOB_ERROR_REQUIRED") }));
    } else {
      setErrors((prev) => ({ ...prev, dob: "" }));
    }
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>
      <DOBTextInputComponent
        testID="billPayDOB"
        isAutoFocus={true}
        value={dob}
        onChangeText={handleManualInput}
        errorText={errors.dob}
        onClearError={() => setErrors((prev) => ({ ...prev, dob: "" }))}
        keyboardType="number-pad"
        maxLength={10}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  }
});

export default BillPayDOBStep;